import React, { useEffect, useState, useCallback } from 'react';
import { axiosInstance } from '../../config/api';
import { fetchDoctorsByClinic, fetchAllDoctors } from 'services/doctorService';

/**
 * ClinicDoctorSelector
 * Props:
 * - isOwner: boolean (owner flow: provide clinicId prop)
 * - isAdmin: boolean (admin flow: show clinic select)
 * - clinicId: string|number (owner's clinic id)
 * - initialDoctorId: string|number optional initial doctor value
 * - onDoctorSelect: function(doctorId)
 * - onClinicSelect: function(clinicId)
 *
 * This component encapsulates fetching clinics and doctors and exposes selects.
 */
const ClinicDoctorSelector = ({ isOwner = false, isAdmin = false, clinicId = null, initialDoctorId = null, onDoctorSelect = () => {}, onClinicSelect = () => {}, showDoctorSelect = true }) => {
  const [clinicsList, setClinicsList] = useState([]);
  const [selectedClinicId, setSelectedClinicId] = useState(clinicId || null);
  const [loadingClinics, setLoadingClinics] = useState(false);

  const [doctorsList, setDoctorsList] = useState([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState(initialDoctorId || null);
  const [loadingDoctors, setLoadingDoctors] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      (async () => {
        setLoadingClinics(true);
        try {
          const resp = await axiosInstance.get('/api/clinic/allClinics');
          const list = Array.isArray(resp.data) ? resp.data : (resp.data.clinics || []);
          setClinicsList(list);
        } catch (e) {
          console.error('[ClinicDoctorSelector] fetchClinics error', e);
          setClinicsList([]);
        } finally {
          setLoadingClinics(false);
        }
      })();
    }
  }, [isAdmin]);

  const fetchDoctors = useCallback(async (cId, autoSelect = false) => {
    if (!cId) return;
    setLoadingDoctors(true);
    try {
      // Prefer clinic-scoped fetch to avoid fetching entire doctor list
      const list = await fetchDoctorsByClinic(cId, { force: false });
      const filtered = Array.isArray(list) ? list : [];
      setDoctorsList(filtered.filter((d) => d.clinic_id === cId || d.clinic_id === parseInt(cId)));
      if (autoSelect && filtered.length) {
        const first = String(filtered[0].id || filtered[0]._id || filtered[0].doctor_id || '');
        setSelectedDoctorId(first);
        onDoctorSelect(first);
      }
    } catch (e) {
      console.error('[ClinicDoctorSelector] fetchDoctors error', e);
      setDoctorsList([]);
    } finally {
      setLoadingDoctors(false);
    }
  }, [onDoctorSelect]);

  // Owner flow: when clinicId prop provided, fetch doctors and auto-select first
  useEffect(() => {
    if (isOwner && clinicId) {
      fetchDoctors(clinicId, true);
    }
  }, [isOwner, clinicId, fetchDoctors]);

  // Admin: when selectedClinicId changes, fetch doctors
  useEffect(() => {
    if (isAdmin && selectedClinicId) {
      fetchDoctors(selectedClinicId, false);
    }
  }, [isAdmin, selectedClinicId, fetchDoctors]);

  // notify parent when doctor selection changes
  useEffect(() => {
    if (selectedDoctorId) onDoctorSelect(selectedDoctorId);
  }, [selectedDoctorId, onDoctorSelect]);

  return (
    <div className="flex items-center gap-3">
      {isAdmin ? (
        <select
          value={selectedClinicId || ''}
          onChange={(e) => { setSelectedClinicId(e.target.value); onClinicSelect(e.target.value); }}
          className="px-3 py-2 rounded-lg border bg-white"
        >
          {loadingClinics ? (
            <option value="">Loading clinics...</option>
          ) : clinicsList && clinicsList.length ? (
            <>
              <option value="">Select clinic</option>
              {clinicsList.map((c) => (
                <option key={c.id || c._id || c.clinic_id} value={(c.id || c._id || c.clinic_id)}>
                  {c.name || c.clinic_name || c.displayName || (`Clinic ${c.id || c._id || ''}`)}
                </option>
              ))}
            </>
          ) : (
            <option value="">No clinics found</option>
          )}
        </select>
      ) : null}

      {showDoctorSelect ? (
        <select
          value={selectedDoctorId || ''}
          onChange={(e) => setSelectedDoctorId(e.target.value)}
          className="px-3 py-2 rounded-lg border bg-white"
        >
          {loadingDoctors ? (
            <option value="">Loading doctors...</option>
          ) : doctorsList && doctorsList.length ? (
            <>
              <option value="">Select doctor</option>
              {doctorsList.map((d) => (
                <option key={d.id || d._id || d.doctor_id} value={(d.id || d._id || d.doctor_id)}>
                  {d.doctor || d.name || d.full_name || (`Dr. ${d.first_name || d.name || d.doctor || ''}`)}
                </option>
              ))}
            </>
          ) : (
            <option value="">No doctors found</option>
          )}
        </select>
      ) : null}
    </div>
  );
};

export default ClinicDoctorSelector;
